/* eslint-disable camelcase */

/**
 * SYNTAX (best practice):
 *
 * path:		/foo
 * state:		foo
 * getter:		foo			this.$store.getters['projects/foo']; (getter)
 * action:		setFoo		this.$store.dispatch('projects/setFoo', { id: fooID }); (action)
 * mutation:	SET_FOO		this.$store.commit('projects/SET_FOO', value); (mutation)
 *
 * 'Actions' are used for async operations (e.g. API calls) and 'mutations' are used for sync operations (e.g. updating state)
 * It's preferable to only use actions in components because they can call multiple mutations and do async operations.
 *
 * In order to use parameters with getters you have to write them as higher order functions:
 *
 * getStatus: state => type => { return state.status[type]; }
 *
 * Then you can call them like this: this.$store.getters['projects/getStatus']('single');
 *
 */

import Vue from 'vue';
import { getField, updateField } from 'vuex-map-fields';

import ProjectAPI from '../api/project.js';
import loadAllProjectsStreaming from '../helpers/loadAllProjectsStreaming.js';

export const projects = {
	namespaced: true,
	state: {
		all_approved: [], // All projects in the database
		all_sandboxed: [], // All projects in the database

		single: {
			id: null,
			hash_id: null,
			title: '',
			title_original: '',
			release_date: null,
			project_number: null,
			opportunity_number: null,
			customer_id: null,
			entity_id: null,
			type_id: 2,
			music_id: null,
			origin_id: null,
			format_id: null,
			format_int_id: null,
			interactive_format_id: null,
			style_id: null,
			department_id: null,
			user_uuid: null,
			online_resource: {
				id: null,
				url: null,
				description: null
			},
			usage_rights: [],
			topics: [],
			uploads: [],
			video_features: [],
			interactive_features: [],
			languages: [],
			languages_objects: []
		},

		filtered: [], // Projects after applying filters

		filteredPageChunk: [], // Filtered projects, sliced for pagination

		currentPage: 1, // The page number corresponding to the filteredPageChunk data

		status: {
			all_approved: 0,
			all_sandboxed: 0,
			filtered: 0,
			filteredPageChunk: 0,
			delete: 0,
			single: 0,
			add: 0,
			edit: 0,
			hiding: 0,
			unhiding: 0
		},

		meta: {
			sandboxed: {
				total: null,
				latest: null,
				loaded: false,
				dirty: false
			},
			approved: {
				total: null,
				latest: null,
				loaded: false,
				dirty: false
			}
		}
	},

	actions: {
		loadAllProjectsStreaming,

		loadPage({ dispatch, commit, state }, payload) {
			// console.groupCollapsed('<(ACTION) loadPage> ' + payload.page);
			// console.trace();
			// console.groupEnd();
			// console.log('START LOAD PAGE');
			commit('SET_FILTERED_PAGE_CHUNK', {
				projects: state.filtered,
				page: payload.page,
				maxProjects: payload.maxProjects
			});
		},

		loadAll({ dispatch, commit, state }, payload) {
			// console.time('loadAll');

			// console.log('<(ACTION) loadAll (' + payload + ')>');
			if (
				(payload === 'sandboxed' && (state.status.all_sandboxed !== 2 || state.meta.sandboxed.dirty)) ||
				(payload === 'approved' && (state.status.all_approved !== 2 || state.meta.approved.dirty))
			) {
				Quasar.plugins.Loading.show({ message: ``, spinner: 'QSpinnerTail' });
				commit('SET_STATUS', { type: 'filtered', status: 1 });
				commit('SET_FILTERED', []);
				if (payload === 'sandboxed') {
					commit('SET_STATUS', { type: 'all_sandboxed', status: 1 });
					var url = 'sandboxed';
				} else if (payload === 'approved') {
					commit('SET_STATUS', { type: 'all_approved', status: 1 });
					url = 'approved';
				}
				return ProjectAPI.getProjects('/api/projects?page=' + url)
					.then(response => {
						Quasar.plugins.Loading.hide();
						if (payload === 'sandboxed') {
							commit('SET_ALL_SANDBOXED', response.data.data);
							commit('SET_STATUS', { type: 'all_sandboxed', status: 2 });
							commit('SET_DIRTY_SANDBOXED', false);
						} else if (payload === 'approved') {
							commit('SET_ALL_APPROVED', response.data.data);
							commit('SET_STATUS', { type: 'all_approved', status: 2 });
							commit('SET_DIRTY_APPROVED', false);
						}
						commit('SET_FILTERED', response.data.data);
						commit('SET_STATUS', { type: 'filtered', status: 2 });
						dispatch('loadPage', { page: 1, maxProjects: 12 });
						// console.timeEnd('loadAll');
						return response;
					})
					.catch(error => {
						Quasar.plugins.Loading.hide();
						if (payload === 'sandboxed') {
							commit('SET_ALL_SANDBOXED', []);
							commit('SET_STATUS', { type: 'all_sandboxed', status: 3 });
						} else if (payload === 'approved') {
							commit('SET_ALL_APPROVED', []);
							commit('SET_STATUS', { type: 'all_approved', status: 3 });
						}
						commit('SET_FILTERED', []);
						commit('SET_STATUS', { type: 'filtered', status: 3 });
						dispatch('loadPage', { page: 1, maxProjects: 12 });
						// console.timeEnd('loadAll');
						return error;
					});
			} else {
				// console.log('WAH???');
				if (payload === 'sandboxed') {
					commit('SET_FILTERED', state.all_sandboxed);
				} else if (payload === 'approved') {
					commit('SET_FILTERED', state.all_approved);
				}
				if (process.env.NODE_ENV !== 'production')
					Vue.ToastSwal.fire('No need!', `Projects are already loaded.`, 'info');
			}
		},

		loadSingle({ commit }, data) {
			// console.log('<(ACTION) loadSingle>');
			commit('SET_STATUS', { type: 'single', status: 1 });

			return ProjectAPI.getProject(data.id)
				.then(function (response) {
					commit('SET_SINGLE', response.data.data);
					commit('SET_STATUS', { type: 'single', status: 2 });
					commit('UPDATE_PROJECT', response.data.data);

					return response;
				})
				.catch(function () {
					commit('SET_SINGLE', {});
					commit('SET_STATUS', { type: 'single', status: 3 });
				});
		},

		updateProject({ commit }, updatedSingleProject) {
			commit('UPDATE_PROJECT', updatedSingleProject);
		},

		addSingle({ commit, state, dispatch }) {
			// console.log('<(ACTION) addSingle>');
			commit('SET_STATUS', { type: 'add', status: 1 });
			// console.log('user_uuid: ' + state.single.user_uuid);
			return ProjectAPI.postAddNewProject(state.single)
				.then(function (response) {
					// console.log('response: ', response);
					const data = response?.data?.[0];
					if (!data) {
						commit('SET_STATUS', { type: 'add', status: 3 });
						console.error('No data returned from the server!', response);
						return response;
					}
					commit('SET_STATUS', { type: 'add', status: 2 });
					commit('SET_SINGLE_ID', data.id);
					dispatch(
						'uploadFiles/uploadFile',
						{
							project_id: data.id,
							project_hash_id: data.hash_id
						},
						{ root: true }
					).then(function (response) {
						if (response) {
							console.log('[uploadFiles/uploadFile] response: ', response);
						} else {
							console.log('[uploadFiles/uploadFile] Nothing to upload, apparently.');
						}
					});
					if (data.hidden === 1) {
						commit('SET_DIRTY_SANDBOXED', true);
						dispatch('loadAll', 'sandboxed');
					} else {
						commit('SET_DIRTY_APPROVED', true);
						dispatch('loadAll', 'approved');
					}
					return response;
				})
				.catch(function (error) {
					console.log('[addSingle] catch: ', error);
					commit('SET_STATUS', { type: 'add', status: 3 });
					return error.response;
				});
		},

		editSingle({ commit, state, dispatch }) {
			// console.log('<(ACTION) editSingle>');
			commit('SET_STATUS', { type: 'edit', status: 1 });
			return ProjectAPI.putEditProject(state.single)
				.then(function (response) {
					commit('SET_STATUS', { type: 'edit', status: 2 });
					dispatch(
						'uploadFiles/uploadFile',
						{
							project_id: response.data.id,
							project_hash_id: response.data.hash_id
						},
						{ root: true }
					).then(function (response) {
						if (response) {
							console.log('[uploadFiles/uploadFile] response: ', response);
						} else {
							console.log('[uploadFiles/uploadFile] Nothing to upload, apparently.');
						}
					});
					if (response.data.hidden === 1) {
						dispatch('loadAll', 'sandboxed');
					} else {
						dispatch('loadAll', 'approved');
					}
					return response;
				})
				.catch(function (error) {
					commit('SET_STATUS', { type: 'edit', status: 3 });
					return error.response;
				});
		},

		deleteSingle({ commit, state, rootState, dispatch }, id) {
			// console.log('<(ACTION) deleteSingle>');
			commit('SET_STATUS', { type: 'delete', status: 1 });
			var projectCollectionType = rootState.route.name;
			ProjectAPI.deleteProject(id)
				.then(function (response) {
					commit('SET_SINGLE', {});
					commit('SET_STATUS', { type: 'delete', status: 2 });
					dispatch('loadAll', projectCollectionType)
						.then(response => {
							// console.log(
							// 	'[ACTION loadAll] SUCCESS: ' + response.status + ' (' + response.statusText + ')'
							// );
							dispatch('loadPage', { page: 1, maxProjects: 12 });
						})
						.catch(error => {
							console.error(
								'[ACTION loadAll] ERROR: ' +
									error.response.status +
									' (' +
									error.response.statusText +
									')'
							);
						});
				})
				.catch(function () {
					commit('SET_STATUS', { type: 'delete', status: 3 });
					commit('SET_DIRTY_SANDBOXED', true);
					commit('SET_DIRTY_APPROVED', true);
					dispatch('loadAll', projectCollectionType);
				});
		},

		hideSingle({ commit, state, dispatch }, id) {
			// console.log('<(ACTION) hideSingle>', id);
			commit('SET_STATUS', { type: 'hiding', status: 1 });
			ProjectAPI.putHideProject(id)
				.then(function (response) {
					commit('SET_STATUS', { type: 'hiding', status: 2 });
				})
				.catch(function (error) {
					console.log(error);
					commit('SET_STATUS', { type: 'hiding', status: 3 });
					commit('SET_DIRTY_SANDBOXED', true);
					commit('SET_DIRTY_APPROVED', true);
					dispatch('loadAll', 'approved');
				});
		},

		clearSingle({ commit }) {
			commit('CLEAR_SINGLE');
		},

		unhideSingle({ commit, state, dispatch }, id) {
			// console.log('<(ACTION) unhideSingle>');
			commit('SET_STATUS', { type: 'unhiding', status: 1 });
			ProjectAPI.putUnhideProject(id)
				.then(function (response) {
					commit('SET_STATUS', { type: 'unhiding', status: 2 });
				})
				.catch(function (error) {
					console.log(error);
					commit('SET_STATUS', { type: 'unhiding', status: 3 });
					commit('SET_DIRTY_SANDBOXED', true);
					commit('SET_DIRTY_APPROVED', true);
					dispatch('loadAll', 'sandboxed');
				});
		},

		moveVirtualProject({ commit, state, rootState, dispatch }, hash_id) {
			// console.log('<(ACTION) moveVirtualProject>', hash_id);
			// var objIndex = state.all.findIndex(obj => obj.hash_id == hash_id);
			// console.log('Project found at index: ' + objIndex);
			if (rootState.route.name === 'sandbox') {
				var result = state.all_sandboxed.splice(
					state.all_sandboxed.findIndex(el => el.hash_id === hash_id),
					1
				);
				commit('SET_FILTERED', state.all_sandboxed);
				result.forEach(function (item) {
					state.all_approved.unshift(item);
				});
			} else if (rootState.route.name === 'projects') {
				result = state.all_approved.splice(
					state.all_approved.findIndex(el => el.hash_id === hash_id),
					1
				);
				commit('SET_FILTERED', state.all_approved);
				result.forEach(function (item) {
					state.all_sandboxed.unshift(item);
				});
			}
			dispatch('loadPage', { page: state.currentPage, maxProjects: 12 });
		},

		removeVirtualProject({ commit, state, rootState, dispatch }, hash_id) {
			// console.log('<(ACTION) removeVirtualProject>', hash_id);
			// var objIndex = state.all.findIndex(obj => obj.hash_id == hash_id);
			// console.log('Project found at index: ' + objIndex);
			if (rootState.route.name === 'sandbox') {
				state.all_sandboxed.splice(
					state.all_sandboxed.findIndex(el => el.hash_id === hash_id),
					1
				);
				commit('SET_FILTERED', state.all_sandboxed);
			} else if (rootState.route.name === 'projects') {
				state.all_approved.splice(
					state.all_approved.findIndex(el => el.hash_id === hash_id),
					1
				);
				commit('SET_FILTERED', state.all_approved);
			}
			dispatch('loadPage', { page: state.currentPage, maxProjects: 12 });
		},

		filterProjectsForEntities({ commit, state, dispatch }, payload) {
			// console.log('<(ACTION) filterProjectsForEntities>');
			if (payload > 0) {
				var result = state.all.filter(e => e.entity.id === payload);
				commit('SET_FILTERED', result);
			} else {
				commit('SET_FILTERED', state.all);
			}
			dispatch('loadPage', { page: 1, maxProjects: 12 });
		},

		filterProjectsForCustomers({ commit, state, dispatch }, payload) {
			// console.log('<(ACTION) filterProjectsForCustomers>');
			// console.log('filterProjectForCustomers Action payload: ' + payload);
			if (payload.length > 0) {
				// var result = state.all.filter(e => e.customer.name == payload);
				var result = state.all.filter(
					o => o.customer.name.toLowerCase().includes(payload.toLowerCase())
					// Object.keys(o).some(k => o[k].toLowerCase().includes(payload.toLowerCase()))
				);
				commit('SET_FILTERED', result);
			} else {
				commit('SET_FILTERED', state.all);
			}
			dispatch('loadPage', { page: 1, maxProjects: 12 });
		},

		filterProjects({ commit, state, dispatch }, payload) {
			// console.log('<(ACTION) filterProjects>');
			var result = state.all;
			if (payload.entity_id > 0) {
				result = result.filter(e => e.entity.id === payload.entity_id);
			}
			if (payload.customerQuery.length > 0) {
				result = result.filter(o =>
					o.customer.name.toLowerCase().includes(payload.customerQuery.toLowerCase())
				);
			}
			if (payload.usageRight) {
				// result = result.filter(function(el) {
				// 	return el.usage_right.indexOf(query.toLowerCase()) !== -1;
				// });
				if (payload.usageRight === 'unknown') {
					result = result.filter(o => o.usage_rights.length === 0);
				} else {
					result = result.filter(
						o =>
							o.usage_rights.filter(obj => {
								return obj.title.toLowerCase().includes(payload.usageRight);
							}).length > 0
					);
				}
			}
			commit('SET_FILTERED', result);
			dispatch('loadPage', { page: 1, maxProjects: 12 });
		}
	},

	mutations: {
		updateField,

		UPDATE_PROJECT(state, updatedSingleProject) {
			// console.log('updatedProject: ', updatedSingleProject);
			let targetCollection;

			if (updatedSingleProject.hidden === 1) {
				targetCollection = state.all_sandboxed;
			} else {
				targetCollection = state.all_approved;
			}
			// all_approved
			// all_sandboxed

			const index = targetCollection.findIndex(project => project.id === updatedSingleProject.id);
			// console.log('index: ', index);
			if (index !== -1) {
				targetCollection.splice(index, 1, updatedSingleProject);
			}
		},

		SET_STATUS(state, payload) {
			// if (payload.type === 'all_approved' && payload.status === 2) {
			// console.trace('🚀 ~ file: projects.js ~ line 412 ~ SET_STATUS ~ SET_STATUS STATE:', state);
			// console.log('🚀 ~ file: projects.js ~ line 412 ~ SET_STATUS ~ SET_STATUS PAYLOAD:', payload);
			// console.log('🚀 ~ file: projects.js ~ line 412 ~ SET_STATUS ~ SET_STATUS PAYLOAD.TYPE:', payload.type);
			// console.log(
			// 	'🚀 ~ file: projects.js ~ line 412 ~ SET_STATUS ~ SET_STATUS PAYLOAD.STATUS:',
			// 	payload.status
			// );
			// }
			state.status[payload.type] = payload.status;
			// if (payload.status == 1) {
			// 	Quasar.plugins.Loading.show({ message: 'Some important process is in progress. Hang on...' });
			// } else {
			// 	Quasar.plugins.Loading.hide();
			// }
		},

		SET_ALL_APPROVED(state, projects) {
			// console.trace(
			// 	'🚀 ~ file: projects.js ~ line 421 ~ SET_ALL_APPROVED ~ SET_ALL_APPROVED',
			// 	state,
			// 	projects,
			// 	projects.length
			// );
			state.all_approved = projects;
		},

		SET_ALL_SANDBOXED(state, projects) {
			state.all_sandboxed = projects;
		},

		SET_META_APPROVED(state, meta) {
			state.meta.approved.total = meta.total;
			state.meta.approved.latest = meta.latest;
		},

		SET_META_SANDBOXED(state, meta) {
			state.meta.sandboxed.total = meta.total;
			state.meta.sandboxed.latest = meta.latest;
		},

		SET_DIRTY_SANDBOXED(state, bool) {
			state.meta.sandboxed.dirty = bool;
		},

		SET_DIRTY_APPROVED(state, bool) {
			state.meta.approved.dirty = bool;
		},

		PUSH_TO_ALL_APPROVED(state, project) {
			// console.count('PUSH_IN');
			state.all_approved.push(project);
			state.filtered.push(project);
		},

		SET_LOADED_SANDBOXED(state, bool) {
			state.meta.sandboxed.loaded = bool;
		},

		SET_LOADED_APPROVED(state, bool) {
			state.meta.approved.loaded = bool;
		},

		PUSH_TO_ALL_SANDBOXED(state, project) {
			// console.count('PUSH_IN');
			// console.log(project);
			state.all_sandboxed.push(project);
			state.filtered.push(project);
		},

		SET_FILTERED(state, projects) {
			state.filtered = projects;
		},

		SET_FILTERED_PAGE_CHUNK(state, payload) {
			// console.log('projects.length: ' + payload.projects.length);
			// console.log('page: ' + payload.page);
			// console.log('maxProjects: ' + payload.maxProjects);
			var offset = (payload.page - 1) * payload.maxProjects;
			var end = payload.page * payload.maxProjects;
			state.filteredPageChunk = payload.projects.slice(offset, end);
		},

		CLEAR_FILTERED(state) {
			state.status.filtered = 1;
			state.filtered = [];
		},

		SET_SINGLE(state, project) {
			state.single = {
				...project,
				...state.single,
				id: project.id,
				hash_id: project.hash_id,
				title: project.title,
				title_original: project.title_original,
				release_date: project.release_date,
				project_number: project.project_number,
				opportunity_number: project.opportunity_number,
				hidden: project.hidden,
				poster: project.poster,
				customer_id: project.customer?.id,
				entity_id: project.entity?.id,
				type_id: project.type?.id,
				music_id: project.music?.id,
				origin_id: project.origin?.id,
				format_id: project.format?.id,
				format_int_id: project.format_int?.id,
				interactive_format_id: project.interactive_format?.id,
				style_id: project.style?.id,
				department_id: project.department?.id,
				topics: project.topics?.map(opt => opt.id),
				video_features: project.video_features?.map(opt => opt.id),
				interactive_features: project.interactive_features?.map(opt => opt.id),
				usage_rights: project.usage_rights?.map(opt => opt.id),
				online_resource: project.online_resource,
				languages: project.languages,
				user: project.user,
				uploads: project.uploads
			};
		},

		SET_CURRENT_PAGE(state, page) {
			state.currentPage = page;
		},

		SET_SINGLE_ID(state, project_id) {
			console.log('## updateProjectID [mutation] ##');
			state.single.id = project_id;
			console.log('state.project.id: ' + state.single.id);
		},

		SET_SPEAKER(state, payload) {
			// console.log(payload);
			//Find index of specific object using findIndex method.
			var objIndex = state.single.languages_objects.findIndex(obj => obj.id === payload.lang_id);

			//Log object to Console.
			// console.log('Before update: ', state.single.languages_objects[objIndex].speaker_id);

			//Update object's name property.
			state.single.languages_objects[objIndex].speaker_id = payload.speaker_id;

			//Log object to console again.
			// console.log('After update: ', state.single.languages_objects[objIndex].speaker_id);
		},

		CLEAR_SINGLE(state) {
			state.single = {
				id: null,
				hash_id: null,
				title: '',
				title_original: '',
				release_date: null,
				project_number: null,
				opportunity_number: null,
				customer_id: null,
				entity_id: null,
				type_id: 2,
				music_id: null,
				origin_id: null,
				format_id: null,
				format_int_id: null,
				interactive_format_id: null,
				style_id: null,
				department_id: null,
				user_uuid: null,
				online_resource: {
					id: null,
					url: null,
					description: null
				},
				usage_rights: [],
				topics: [],
				uploads: [],
				video_features: [],
				interactive_features: [],
				languages: [],
				languages_objects: []
			};
			// console.log('store.single cleared!');
		}
	},

	getters: {
		getField,

		getCookie: state => name => {
			var value = '; ' + document.cookie;
			var parts = value.split('; ' + name + '=');
			if (parts.length === 2) return parts.pop().split(';').shift();
		},

		getNumberOfFiltered: state => {
			return state.filtered.length;
		},

		getStatus: state => type => {
			return state.status[type];
		},

		getAllApproved(state) {
			return state.all_approved;
		},

		getAllSandboxed(state) {
			return state.all_sandboxed;
		},

		getMetaApproved(state) {
			return state.meta.approved;
		},

		getMetaSandboxed(state) {
			return state.meta.sandboxed;
		},

		getLoadedSandboxed(state) {
			return state.meta.sandboxed.loaded;
		},

		getLoadedApproved(state) {
			return state.meta.approved.loaded;
		},

		getFiltered(state) {
			return state.filtered;
		},

		getFilteredPageChunk(state) {
			return state.filteredPageChunk;
		},

		getSingle(state) {
			return state.single;
		},

		getCurrentPage(state) {
			return state.currentPage;
		},

		getLastPage(state) {
			return Math.ceil(state.filtered.length / 12);
		}
	}
};
